/**
 * Split a note's text into the pieces the CRM renders: plain text, links to
 * web addresses, and highlighted @mentions (spec crm-note-text-links).
 *
 * The tokens are rendered as Vue nodes by NoteText.vue, never as an HTML
 * string, so nothing here escapes anything: Vue escapes text and binds `href`
 * as an attribute, which is what keeps note content from adding markup.
 */
export type NoteTextToken =
    { kind: 'text'; text: string } | { kind: 'link'; text: string; href: string } | { kind: 'mention'; text: string };

// An explicit http(s) scheme only: bare domains, emails and every other scheme
// stay text. `<`, `>` and `"` end an address because none is valid unencoded in
// a URL, and they are what markup staged around an address would use.
const ADDRESS = /https?:\/\/[^\s<>"]+/gi;

// Includes the curly quotes, guillemet and ellipsis that smart punctuation and
// pasted email text put after an address.
const TRAILING_PUNCTUATION = '.,;:!?\'"”’»…';

const OPENING_BRACKET: Record<string, string> = { ')': '(', ']': '[', '}': '{', '）': '（', '］': '［', '｝': '｛' };

/**
 * Drop sentence punctuation, and a closing bracket the address never opened,
 * from the end of a matched address.
 *
 * Characters are counted once and the counts kept as the end moves, so a long
 * run of closing brackets trims in linear time.
 */
function trimAddress(candidate: string): string {
    const counts = new Map<string, number>();

    for (const character of candidate) {
        counts.set(character, (counts.get(character) ?? 0) + 1);
    }

    let end = candidate.length;

    while (end > 0) {
        const last = candidate[end - 1];
        const opener = OPENING_BRACKET[last];
        const unopened = opener !== undefined && (counts.get(last) ?? 0) > (counts.get(opener) ?? 0);

        if (!TRAILING_PUNCTUATION.includes(last) && !unopened) {
            break;
        }

        counts.set(last, (counts.get(last) ?? 0) - 1);
        end -= 1;
    }

    return candidate.slice(0, end);
}

function isWebAddress(address: string): boolean {
    try {
        const url = new URL(address);

        return (url.protocol === 'http:' || url.protocol === 'https:') && url.host !== '';
    } catch {
        return false;
    }
}

/**
 * Highlight each `@{full name}` in text that holds no address. Longer names are
 * tried first, so "@Jane Smithson" is not cut short by "@Jane Smith".
 */
function tokeniseMentions(text: string, mentionNames: readonly string[]): NoteTextToken[] {
    const mentions = [...new Set(mentionNames.filter((name) => name !== '').map((name) => `@${name}`))].sort(
        (a, b) => b.length - a.length,
    );

    if (mentions.length === 0) {
        return text === '' ? [] : [{ kind: 'text', text }];
    }

    const pattern = new RegExp(
        mentions.map((mention) => mention.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
        'g',
    );
    const tokens: NoteTextToken[] = [];
    let cursor = 0;

    for (const match of text.matchAll(pattern)) {
        if (match.index > cursor) {
            tokens.push({ kind: 'text', text: text.slice(cursor, match.index) });
        }

        tokens.push({ kind: 'mention', text: match[0] });
        cursor = match.index + match[0].length;
    }

    if (cursor < text.length) {
        tokens.push({ kind: 'text', text: text.slice(cursor) });
    }

    return tokens;
}

/**
 * @param text the note text, already decoded: what the reader should see
 * @param mentionNames full names of the staff the note mentions
 */
export function tokeniseNoteText(text: string, mentionNames: readonly string[] = []): NoteTextToken[] {
    const tokens: NoteTextToken[] = [];
    let cursor = 0;

    // Addresses are found first and mentions only in the text between them, so a
    // mentioned name inside an address never splits the link.
    for (const match of text.matchAll(ADDRESS)) {
        const address = trimAddress(match[0]);

        if (!isWebAddress(address)) {
            continue;
        }

        tokens.push(...tokeniseMentions(text.slice(cursor, match.index), mentionNames));
        tokens.push({ kind: 'link', text: address, href: address });
        cursor = match.index + address.length;
    }

    tokens.push(...tokeniseMentions(text.slice(cursor), mentionNames));

    return tokens;
}
